import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { listAlerts } from "../../services/alertService";
import { AlertCard } from "../organisms/AlertCard";
import type { AlertWithContext } from "../../types/models";

interface AlertCenterSlideUpProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Mobile slide-up panel for alert center (AC7 – Story 6.3).
 * Shows top 5 alerts and "View all" link; does not touch main alerts store.
 */
export function AlertCenterSlideUp({ isOpen, onClose }: AlertCenterSlideUpProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AlertWithContext[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    listAlerts({ page: 1, limit: 5, status: "active" })
      .then((res) => {
        setAlerts(res.items);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : t("failed_to_load_alerts"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen]);

  const handleViewAll = () => {
    onClose();
    navigate("/alerts");
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="md:hidden fixed inset-0 bg-black/50 dark:bg-black/60 z-40"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="md:hidden fixed start-0 end-0 bottom-0 top-[20%] z-50 bg-white dark:bg-neutral-800 rounded-t-2xl shadow-xl flex flex-col transition-transform duration-300 ease-out"
        role="dialog"
        aria-label={t("alert_center")}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-neutral-200 dark:border-neutral-700 shrink-0">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {t("alerts")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
            aria-label={t("close_alert_center")}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 py-4">{error}</p>
          )}
          {!loading && !error && alerts.length === 0 && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 py-4">
              {t("no_active_alerts")}
            </p>
          )}
          {!loading && !error && alerts.length > 0 && (
            <ul className="space-y-3 pb-4" role="list">
              {alerts.map((alert) => (
                <li key={alert.id}>
                  <AlertCard alert={alert} />
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={handleViewAll}
            className="w-full py-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t("view_all_alerts")}
          </button>
        </div>
      </div>
    </>
  );
}
